import { cloneDeep, isString, isUndefined } from 'lodash';
import { convertMaterialIdsToObj, deepDeletePrivateProps, getBehavior, parseLayerId } from '@/framework';
import { Framework } from '@/framework/instance';
import { drawAIWatermark } from '@/utils/ai-watermark';
import { createCanvas, getDrawContainParams, loadImageAsync } from '@/utils/file-kit';
import {
    IBackgroundLayerProps,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    IResult,
    IResultCanvas,
    LayerType,
} from '@/version-types';

export class Result extends Framework {
    isAbort = false;

    private stepEventName = 'gs-download-step';

    /**
     * 中止合成
     */
    abort() {
        this.isAbort = true;
    }

    /**
     * 获取中止合成结果
     */
    private getAbortResult() {
        if (this.isAbort) {
            const { canvas } = createCanvas(10, 10);
            return {
                cover: canvas,
                slices: [],
            };
        }
        return null;
    }

    /**
     * 监听合成进度
     *
     * @param callback - 回调处理方法
     */
    onStep(callback: (args: { current: number; total: number }) => void) {
        return this.framework.events.on(this.stepEventName, callback);
    }

    /**
     * 保存为图片
     *
     * @param coverStagesCount - 可选；长图封面，需要的画布个数。 1: 第一个画布的做为封面，【默认】全部长图
     * @param extension - 可选；图片格式；默认值为 `jpg`
     * @param pages - 可选；需要合成的页面索引数组，默认全部合成
     * @param puzzleCover - 使用7页拼图模式生成封面，需要每一页尺寸相同；默认值为 `false`
     * @param fullPageNoUseLastPage - 是否不使用最后一页；默认值为 `false`
     *
     */
    async drawStages(
        coverStagesCount?: number,
        extension = 'jpg',
        pages?: number[],
        puzzleCover = false,
        fullPageNoUseLastPage = false,
        showStep = true,
        needCopyrightWaterMark = false,
    ) {
        extension = extension.toLowerCase();
        this.isAbort = false;
        const pagesSet = new Set(pages || []);
        const fullCover = isUndefined(coverStagesCount);
        const slices: IResultCanvas[] = [];

        let resWidth = 0;
        let resHeight = 0;
        let preHeight = 0;
        let cover: HTMLCanvasElement | null = null;
        let finalCoverStages = 0;
        let stepIndex = 0;

        const stages = this.framework.getStages();

        for (let i = 0; i < stages.length; i++) {
            if (pagesSet.size && !pagesSet.has(i)) continue;
            const layers = stages[i];
            const { width, height } = layers[0];
            resWidth = Math.max(resWidth, width);
            resHeight += height;
            if (!fullCover && finalCoverStages < coverStagesCount) {
                preHeight += height;
                finalCoverStages++;
            }
            const canvas = await this.drawLayers(layers, extension);
            /** 需要添加额外版权水印的舞台
             *  只有一个图片图层时，并且不是自己上传的图
             */
            if (needCopyrightWaterMark) {
                try {
                    const ctx = canvas.getContext('2d');
                    const needCopyrightStage =
                        (layers.length === 1 && (layers[0].data as IBackgroundLayerProps).url) ||
                        (layers.length === 2 &&
                            !(layers[0].data as IBackgroundLayerProps).url &&
                            layers[1].layerType === LayerType.IMAGE_LAYER &&
                            (layers[1].data as IImageLayerProps).url.indexOf('xiuxiu-pro.meitudata.com/posters') ===
                                -1);

                    if (ctx && needCopyrightStage) {
                        // 加载并绘制水印图片
                        const img = await loadImageAsync(
                            'https://public.static.meitudata.com/xiuxiu-pc/image-editor-sdk/watermark-bg.png',
                            false,
                        );
                        // 计算缩放比例 - 以1246为基准进行等比缩放
                        const baseWidth = 1246;
                        const scale = width / baseWidth;
                        const scaledImgWidth = img.width * scale;
                        const scaledImgHeight = img.height * scale;
                        const dx = width - scaledImgWidth - 20 * scale;
                        const dy = height - scaledImgHeight - 20 * scale;
                        ctx.drawImage(img, dx, dy, scaledImgWidth, scaledImgHeight);

                        ctx.font = `${Math.floor(30 * scale)}px Arial`;
                        ctx.fillStyle = '#ffffff'; // 设置文字颜色为红色，使其更明显
                        // 绘制文字
                        ctx.fillText('应版权方要求，请勿单独使用本图片素材', dx + 66 * scale, dy + 46 * scale);
                    }
                } catch (e) {
                    console.log('版权水印添加失败', e);
                }
            }

            slices.push({ width, height, canvas, index: i });
            if (showStep) {
                this.framework.events.emit(this.stepEventName, {
                    current: ++stepIndex,
                    total: pagesSet.size || stages.length,
                });
            }
            const abortResult = this.getAbortResult();
            if (abortResult) return abortResult;
        }
        // 7页拼图封面，需外扩
        if (puzzleCover) {
            resWidth += 1;
            resHeight = slices[0].height * 2; // 总的间距
        }
        if (slices.length === 1 || coverStagesCount === 1) {
            cover = slices[0].canvas;
        } else {
            const { canvas, ctx } = createCanvas(resWidth, fullCover ? resHeight : preHeight);
            const len = fullCover ? slices.length : coverStagesCount;
            let dy = 0;
            for (let i = 0; i < len; i++) {
                const { width, height, canvas } = slices[i] || {};
                if (!canvas) continue;
                // 5页拼图封面绘图，需要取最后一页
                if (puzzleCover) {
                    let index = i;
                    if (i >= 4 && !fullPageNoUseLastPage) {
                        if (i !== len - 1) continue;
                        index = 4;
                    }
                    // 除第一页外的新增行数
                    const lineCount = ~~((index - 1) / 2);
                    const dx = index ? (index % 2 ? 0 : width / 2 + 1) : 0;
                    const dy = index ? (lineCount / 2 + 1) * height + lineCount + 2 : 0;
                    const dWidth = index ? width / 2 : width + 1;
                    const dHeight = index ? height / 2 : height + 1;
                    ctx.drawImage(
                        canvas, // img,
                        0,
                        0,
                        width,
                        height,
                        dx,
                        dy,
                        dWidth,
                        dHeight,
                    );
                    const lineWidth = 10;
                    // 绘制边框
                    ctx.beginPath();
                    ctx.lineWidth = lineWidth;
                    ctx.strokeStyle = 'rgba(255,255,255,.7)';
                    switch (index) {
                        case 0:
                            ctx.moveTo(0, dHeight - 1);
                            ctx.lineTo(dWidth, dHeight - 1);
                            break;
                        case 1:
                        case 3:
                            ctx.moveTo(0, dy + 1);
                            ctx.lineTo(dWidth - 1, dy + 1);
                            ctx.lineTo(dWidth - 1, dy + dHeight - 1);
                            if (index === 1) ctx.lineTo(0, dy + dHeight - 1);
                            break;
                        case 2:
                        case 4:
                            ctx.moveTo(resWidth, dy + 1);
                            ctx.lineTo(dx + 1, dy + 1);
                            ctx.lineTo(dx + 1, dy + dHeight - 1);
                            if (index === 2) ctx.lineTo(resWidth, dy + dHeight - 1);
                            break;
                        default:
                            break;
                    }
                    ctx.stroke();
                } else {
                    ctx.drawImage(canvas, 0, 0, width, height, 0, dy, width, height);
                    dy += height;
                }
            }
            cover = canvas;
            if (extension === 'jpg') {
                // 长图刷白底
                const { canvas: whiteCanvas, ctx: whiteCtx } = createCanvas(
                    resWidth,
                    fullCover ? resHeight : preHeight,
                );
                whiteCtx.fillStyle = '#fff';
                whiteCtx.fillRect(0, 0, resWidth, fullCover ? resHeight : preHeight);
                whiteCtx.drawImage(cover, 0, 0);
                cover = whiteCanvas;
            }
        }
        return {
            cover,
            slices,
        };
    }

    /**
     * 获取指定图层配方结果
     * @param args - 图层id 或 图层数据
     */
    async getLayerResult(args: string | ILayerProps) {
        const layer = isString(args) ? this.framework.getLayer(args, true) : cloneDeep(args);
        if (this.framework.checkIsGroup(layer)) {
            (layer.data as IGroupLayerProps).isExtraGroup = false;
        }
        await this.formateResult([layer], false, false);
        return layer;
    }

    /**
     * 绘制指定的图层
     * @param layers - 图层数据
     * @param extension - 可选；图片格式；默认值为 `jpg`
     * @param noBg - 可选；是否不绘制背景；默认值为 `false`
     */
    async drawLayers(layers: ILayerProps[], extension = 'jpg', noBg = false) {
        const startTime = new Date().getTime();
        extension = extension.toLowerCase();
        const { width, height } = layers[0];
        const { canvas: stageCanvas, ctx: stageCtx } = createCanvas(width, height);
        await this.framework.createFontFamilySubset(layers, false);
        const isValidLayer = (layer: ILayerProps) => {
            const valid = !layer.hidden && !layer.__isHidden && layer.width > 0 && layer.height > 0;
            return valid;
        };
        const drawLayer = async (layer: ILayerProps) => {
            if (!isValidLayer(layer)) {
                console.log('无效图层', layer);
                return;
            }
            if (this.framework.checkIsGroup(layer)) {
                const children = layer.children || [];
                for (let i = 0, len = children.length; i < len; i++) {
                    await drawLayer(children[i]);
                }
            }

            // TODO: 临时解决下载时，主图的效果没有应用最新效果的问题
            await getBehavior(this.identity, layer)?.onBeforeSaveAsync(layer, false);

            const result = await getBehavior(this.identity, layer)?.getResultCanvasAsync(layer, {
                width,
                height,
            });

            if (result) {
                stageCtx.drawImage(result, 0, 0);
            }
        };
        for (let i = 0, len = layers.length; i < len; i++) {
            const layer = layers[i];
            if (noBg && this.framework.checkIsBackground(layer)) {
                continue;
            }
            await drawLayer(layer);
        }
        if (layers[1] && this.framework.checkIsMain(layers[1])) {
            const resultMaskURL = (layers[1].data as IImageLayerProps).maskURL;
            if (resultMaskURL) {
                const { canvas, ctx } = createCanvas(width, height);
                const maskImage = await loadImageAsync(resultMaskURL);

                // ATTENTION: 主图的蒙版目前是没有涉及 minimumMask 的概念，应该永远走跟画布一样尺寸的绘制方式
                // if ((layers[1].data as IImageLayerProps).minimumMask) {
                ctx.drawImage(maskImage, 0, 0, width, height);
                // } else {
                //     const { width: maskWidth, height: maskHeight } = maskImage;
                //     const res = getDrawContainParams(maskWidth, maskHeight, width, height);
                //     ctx.drawImage(maskImage, res.dx, res.dy, res.dw, res.dh);
                // }

                stageCtx.globalCompositeOperation = 'destination-in';
                stageCtx.drawImage(canvas, 0, 0);
            }
        }

        let resultCanvas: HTMLCanvasElement;
        // 绘制白底
        if (extension === 'jpg' || extension === 'jpeg') {
            const { canvas: whiteCanvas, ctx: whiteCtx } = createCanvas(width, height);
            whiteCtx.fillStyle = '#fff';
            whiteCtx.fillRect(0, 0, width, height);
            whiteCtx.drawImage(stageCanvas, 0, 0);
            resultCanvas = whiteCanvas;
        } else {
            resultCanvas = stageCanvas;
        }
        const stopTime = new Date().getTime();
        console.log(`合成结束 `, stopTime - startTime);

        return resultCanvas;
    }

    /**
     * 缩放图片，使用 contain 绘制
     *
     * @param targetCanvas - 待缩放的画布
     * @param destWidth - 目标宽度
     * @param destHeight - 目标高度
     */
    scaleContain(targetCanvas: HTMLCanvasElement, destWidth: number, destHeight: number) {
        const { width: canvasW, height: canvasH } = targetCanvas;
        const res = getDrawContainParams(canvasW, canvasH, destWidth, destHeight);
        const { canvas, ctx } = createCanvas(destWidth, destHeight);
        ctx.drawImage(targetCanvas, res.dx, res.dy, res.dw, res.dh);
        return canvas;
    }

    /**
     * 获取舞台数据
     *
     * - 用于保存到服务端到配方数据
     *
     * @param isAutoSave  - 可选；是否自动保存；默认值为 `false`
     */
    async getResult(isAutoSave = false, isDuplicate = false) {
        const stages = this.framework.getStages();
        const extraInfos = this.framework.getExtraInfoList();
        const count = this.framework.getRenderStageLength();
        const result: IResult[] = [];
        for (let i = 0; i < count; i++) {
            const layers = stages[i];
            await this.formateResult(layers, isAutoSave, isDuplicate);
            const extraInfo = extraInfos[i];
            const { width, height } = layers[0];
            deepDeletePrivateProps(extraInfo);
            const res: IResult = {
                ...extraInfo,
                width,
                height,
                layers,
            };
            result.push(res);
        }
        return result;
    }

    /**
     * 格式化结果
     *
     * @param layers - 图层列表
     * @param isAutoSave - 可选；是否自动保存；默认值为 `false`
     */
    async formateResult(layers: ILayerProps[], isAutoSave: boolean, isDuplicate: boolean) {
        const convert = async (layer: ILayerProps) => {
            if (this.framework.checkIsGroup(layer)) {
                (layer.data as IGroupLayerProps).isExtraGroup = false;
                const children = layer.children || [];
                for (let i = 0, len = children.length; i < len; i++) {
                    await convert(children[i]);
                }
            }
            await getBehavior(this.identity, layer)?.onBeforeSaveAsync(layer, isAutoSave, isDuplicate);
            layer.id = parseLayerId(layer.id).id;
            convertMaterialIdsToObj(layer);
            deepDeletePrivateProps(layer);
        };

        for (let i = 0, len = layers.length; i < len; i++) {
            const layer = layers[i];
            await convert(layer);
        }
    }
}
